import { AccountInfo } from '@azure/msal-browser';
import {
  AuthenticatedTemplate,
  MsalProvider,
  UnauthenticatedTemplate,
  useAccount,
  useIsAuthenticated,
  useMsal,
} from '@azure/msal-react';
import React, { useContext } from 'react';
import { z } from 'zod';
import { createMsalBrowserPublicClientApplication } from '@/features/auth/createMsalBrowserPublicClientApplication';
import { SsoAuthFlow } from '@/features/auth/SsoAuthFlow';
import { isLocal } from '@/lib/isLocal';

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
}

export const AuthContext = React.createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
});

const User = z.object({
  email: z.string(), // could be any string given by Entra ID configuration
  name: z.string().optional(),
});

export type User = z.infer<typeof User>;

/**
 * Fetch user information from MSAL account.
 *
 * In this case we don't receive account from `useMsal`
 *
 * @param account
 */
const getUserFromAccountInfo = (account: AccountInfo | null): User | null => {
  if (null === account) {
    return null;
  }

  const { username: email, name } = account;

  const validated = User.safeParse({ email, name });

  return validated.success ? validated.data : null;
};

const InternalAuthProvider = ({
  children,
  context,
}: {
  children: React.ReactNode;
  context: AuthContextType;
}) => <AuthContext.Provider value={context}>{children}</AuthContext.Provider>;

const LocalAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const context: AuthContextType = {
    user: {
      name: 'LOCAL_USER',
      email: 'LOCAL_EMAIL',
    },
    isAuthenticated: true,
  };
  return (
    <InternalAuthProvider context={context}>{children}</InternalAuthProvider>
  );
};

/**
 * Delegates auth information from initialized MSAL instance to auth context
 * It must be wrapped in MsalProvider, to ensure msal instance initialization before using msal hooks.
 *
 * When no account exists, then delegate to auth flow.
 *
 * @param children
 * @constructor
 */
const MsalToAuthContextDelegation = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const isAuthenticated = useIsAuthenticated();
  if (!isAuthenticated) {
    return <SsoAuthFlow></SsoAuthFlow>;
  }
  const { accounts, instance } = useMsal();
  if (!instance.getActiveAccount() && accounts.length > 0) {
    instance.setActiveAccount(accounts[0]);
  }
  const account = useAccount();
  const user = getUserFromAccountInfo(account);
  const authContext = {
    user,
    isAuthenticated,
  };

  return (
    <InternalAuthProvider context={authContext}>
      {children}
    </InternalAuthProvider>
  );
};

const MsalAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const publicClientApplication = createMsalBrowserPublicClientApplication();
  return (
    <MsalProvider instance={publicClientApplication}>
      <AuthenticatedTemplate>
        <MsalToAuthContextDelegation>{children}</MsalToAuthContextDelegation>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SsoAuthFlow></SsoAuthFlow>
      </UnauthenticatedTemplate>
    </MsalProvider>
  );
};

/**
 * Use local auth or msal depending on environment
 * @param children
 * @constructor
 */
export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  if (false === isLocal()) {
    return <MsalAuthProvider>{children}</MsalAuthProvider>;
  } else {
    return <LocalAuthProvider>{children}</LocalAuthProvider>;
  }
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
